import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Search } from 'lucide-react';
import {
    type KeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { OrganizationMenu } from '@apps/web/components/layout/organization_menu';
import { ThemeToggleRow } from '@apps/web/components/layout/theme_toggle';
import {
    searchSettingsNav,
    searchSettingsNavGroups,
    type SettingsNavGates,
    type SettingsNavGroup,
    type SettingsNavItem,
    settingsNavPathIsActive,
    type SettingsNavSearchResult,
} from '@apps/web/components/settings/settings_nav_items';
import { Input } from '@apps/web/components/ui/input';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarRail,
    SidebarTrigger,
    useSidebar,
} from '@apps/web/components/ui/sidebar';
import { cn } from '@apps/web/lib/utils';

export type SettingsSidebarProps = {
    readonly gates: SettingsNavGates;
    readonly onSearchPerformed?: (query: string, resultCount: number) => void;
    readonly onTabSelected?: (item: SettingsNavItem) => void;
};

function NavSection({
    group,
    currentPath,
    onNavigate,
    onTabSelected,
}: {
    readonly group: SettingsNavGroup;
    readonly currentPath: string;
    readonly onNavigate?: () => void;
    readonly onTabSelected?: (item: SettingsNavItem) => void;
}) {
    const { isMobile, setOpenMobile } = useSidebar();

    return (
        <div>
            {group.label !== '' && (
                <h3 className="text-muted-foreground/80 mb-2 text-[11px] font-semibold">
                    {group.label}
                </h3>
            )}
            <SidebarMenu className="gap-0.5">
                {group.items.map((item) => (
                    <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton
                            asChild={true}
                            isActive={settingsNavPathIsActive(
                                item.to,
                                currentPath,
                            )}
                            size="sm"
                            className="text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-foreground [&>svg]:text-muted-foreground data-[active=true]:[&>svg]:text-sidebar-foreground w-full gap-2.5 px-2.5 py-1.5"
                        >
                            <Link
                                to={item.to}
                                onClick={() => {
                                    onTabSelected?.(item);
                                    onNavigate?.();
                                    if (isMobile) {
                                        setOpenMobile(false);
                                    }
                                }}
                                className="flex items-center leading-4 transition-colors"
                            >
                                <item.icon className="h-4 w-4" />
                                <span className="text-[12px] font-medium">
                                    {item.label}
                                </span>
                            </Link>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                ))}
            </SidebarMenu>
        </div>
    );
}

type GroupedSearchResult = {
    readonly item: SettingsNavItem;
    readonly sections: ReadonlyArray<string>;
};

function groupSearchResults(
    results: ReadonlyArray<SettingsNavSearchResult>,
): ReadonlyArray<GroupedSearchResult> {
    const groups = new Map<
        string,
        { item: SettingsNavItem; sections: Array<string> }
    >();
    for (const result of results) {
        let group = groups.get(result.item.to);
        if (!group) {
            group = { item: result.item, sections: [] };
            groups.set(result.item.to, group);
        }
        if (
            result.section !== null &&
            !group.sections.includes(result.section)
        ) {
            group.sections.push(result.section);
        }
    }
    return Array.from(groups.values());
}

function SearchResultList({
    activeIndex,
    currentPath,
    groups,
    onNavigate,
    onTabSelected,
}: {
    // Index into the flattened row list (each group's tab row followed by
    // its section rows) of the keyboard-highlighted row.
    readonly activeIndex: number;
    readonly currentPath: string;
    readonly groups: ReadonlyArray<GroupedSearchResult>;
    readonly onNavigate: () => void;
    readonly onTabSelected?: (item: SettingsNavItem) => void;
}) {
    const { isMobile, setOpenMobile } = useSidebar();

    // Keep the keyboard-highlighted row visible when arrowing past the
    // edge of the scrollable sidebar.
    const scrollActiveRowIntoView = useCallback(
        (node: HTMLAnchorElement | null) => {
            node?.scrollIntoView({ block: 'nearest' });
        },
        [],
    );

    // Flat row index of each group's tab row, in render order.
    let nextRowIndex = 0;
    const groupRows = groups.map((group) => {
        const tabRowIndex = nextRowIndex;
        nextRowIndex += 1 + group.sections.length;
        return { group, tabRowIndex };
    });

    return (
        <div className="space-y-2">
            {groupRows.map(({ group, tabRowIndex }) => (
                <div key={group.item.to}>
                    <SidebarMenu className="gap-0">
                        <SidebarMenuItem>
                            <SidebarMenuButton
                                asChild={true}
                                isActive={currentPath === group.item.to}
                                size="sm"
                                className={cn(
                                    'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-foreground [&>svg]:text-muted-foreground data-[active=true]:[&>svg]:text-sidebar-foreground h-auto w-full gap-2.5 px-2.5 py-2',
                                    tabRowIndex === activeIndex &&
                                        'bg-sidebar-accent text-sidebar-foreground',
                                )}
                            >
                                <Link
                                    ref={
                                        tabRowIndex === activeIndex
                                            ? scrollActiveRowIntoView
                                            : undefined
                                    }
                                    to={group.item.to}
                                    onClick={() => {
                                        onTabSelected?.(group.item);
                                        onNavigate();
                                        if (isMobile) {
                                            setOpenMobile(false);
                                        }
                                    }}
                                    className="flex items-center gap-2.5 leading-4 transition-colors"
                                >
                                    <group.item.icon className="h-4 w-4 shrink-0" />
                                    <span className="text-[12px] font-medium">
                                        {group.item.label}
                                    </span>
                                </Link>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>
                    <div className="border-border ml-6 space-y-0.5 border-l pl-2">
                        {group.sections.map((section, sectionIndex) => {
                            const rowIndex = tabRowIndex + 1 + sectionIndex;
                            return (
                                <Link
                                    key={`${group.item.to}/${section}`}
                                    ref={
                                        rowIndex === activeIndex
                                            ? scrollActiveRowIntoView
                                            : undefined
                                    }
                                    to={group.item.to}
                                    onClick={() => {
                                        onTabSelected?.(group.item);
                                        onNavigate();
                                        if (isMobile) {
                                            setOpenMobile(false);
                                        }
                                    }}
                                    className={cn(
                                        'text-muted-foreground hover:text-foreground block rounded-md px-2.5 py-1.5 text-[11px] transition-colors',
                                        rowIndex === activeIndex &&
                                            'bg-sidebar-accent text-foreground',
                                    )}
                                >
                                    {section}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}

export function SettingsSidebar(props: SettingsSidebarProps) {
    const { gates, onSearchPerformed, onTabSelected } = props;
    const location = useLocation();
    const navigate = useNavigate();
    const { isMobile, setOpenMobile } = useSidebar();
    const currentPath = location.pathname;
    const [query, setQuery] = useState('');
    const [activeResultIndex, setActiveResultIndex] = useState(0);
    const trimmedQuery = query.trim();
    const isSearching = trimmedQuery !== '';
    // While searching, this returns only the sections with a match (each
    // trimmed to its matching items); empty query returns the full visible nav.
    const groups = useMemo(
        () => searchSettingsNavGroups(trimmedQuery, gates),
        [gates, trimmedQuery],
    );
    const searchResults = useMemo(
        () => (isSearching ? searchSettingsNav(trimmedQuery, gates) : []),
        [gates, isSearching, trimmedQuery],
    );
    const groupedResults = useMemo(
        () => groupSearchResults(searchResults),
        [searchResults],
    );
    // The keyboard-navigable rows in render order: each group's tab row
    // followed by its section rows. Every row navigates to its group's tab.
    const resultRowItems = useMemo(
        () =>
            groupedResults.flatMap((group) => [
                group.item,
                ...group.sections.map(() => group.item),
            ]),
        [groupedResults],
    );
    useEffect(() => {
        if (!isSearching) {
            return;
        }
        const timeoutId = window.setTimeout(() => {
            onSearchPerformed?.(trimmedQuery, searchResults.length);
        }, 500);
        return () => window.clearTimeout(timeoutId);
    }, [isSearching, onSearchPerformed, searchResults.length, trimmedQuery]);

    function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (!isSearching || resultRowItems.length === 0) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveResultIndex((index) =>
                Math.min(index + 1, resultRowItems.length - 1),
            );
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveResultIndex((index) => Math.max(index - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const item =
                resultRowItems[
                    Math.min(activeResultIndex, resultRowItems.length - 1)
                ];
            if (!item) {
                return;
            }
            onTabSelected?.(item);
            setQuery('');
            if (isMobile) {
                setOpenMobile(false);
            }
            void navigate({ to: item.to });
        }
    }

    return (
        <Sidebar
            collapsible="offcanvas"
            className="animate-in fade-in-0 duration-150 [--sidebar:transparent] motion-reduce:animate-none"
        >
            <SidebarHeader className="p-[9px]">
                <div className="flex items-center justify-between gap-1">
                    <Link
                        to="/"
                        className="text-foreground hover:bg-accent inline-flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-md px-1 text-left text-[15px] font-semibold transition-colors"
                        aria-label="Back to main app"
                    >
                        <ArrowLeft className="text-muted-foreground h-4 w-4 shrink-0" />
                        <h2 className="truncate text-[15px] font-semibold">
                            Settings
                        </h2>
                    </Link>
                    <SidebarTrigger className="text-muted-foreground hover:text-foreground h-7 w-7 cursor-pointer p-0" />
                </div>
                <OrganizationMenu traceReason="settings-sidebar" />
            </SidebarHeader>
            <SidebarContent className="px-3 pt-1 pb-4">
                <div className="space-y-4">
                    <div className="relative">
                        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2" />
                        <Input
                            type="search"
                            value={query}
                            onChange={(event) => {
                                setQuery(event.target.value);
                                setActiveResultIndex(0);
                            }}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Search settings…"
                            aria-label="Search settings"
                            className="bg-background h-8 pr-2 pl-8 text-[12px] shadow-none"
                        />
                    </div>
                    {isSearching && searchResults.length === 0 ? (
                        <p
                            className="text-muted-foreground px-2.5 py-1.5 text-[12px]"
                            data-testid="settings-search-results"
                        >
                            No settings found.
                        </p>
                    ) : isSearching ? (
                        <div data-testid="settings-search-results">
                            <SearchResultList
                                activeIndex={activeResultIndex}
                                currentPath={currentPath}
                                groups={groupedResults}
                                onNavigate={() => setQuery('')}
                                onTabSelected={onTabSelected}
                            />
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {groups.map((group) => (
                                <NavSection
                                    key={`${group.scope}/${group.label}`}
                                    group={group}
                                    currentPath={currentPath}
                                    onNavigate={
                                        isSearching
                                            ? () => setQuery('')
                                            : undefined
                                    }
                                    onTabSelected={onTabSelected}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </SidebarContent>
            <SidebarFooter>
                <ThemeToggleRow variant="sidebar" />
            </SidebarFooter>
            <SidebarRail />
        </Sidebar>
    );
}
